import React, {useEffect} from 'react';
import {Divider, Icon, Select, view} from '@sail/ui';
import {cancel, document, download} from '@sail/icons/react/Icon';
import {useIntl} from '@sail/intl';
import {useUploadFile} from './useUploadFile';
import {useRetrieveFile} from './useRetrieveFile';
import {messages} from './messages';
import {styles} from './styles';
import {UploadedFileErrorState} from './UploadedFileErrorState';
import {UploadedFileLoadingState} from './UploadedFileLoadingState';
import {Button} from '../../SharedComponents/Button/Button';
import type {StripeFileFragmentData} from './stripeFileFragment';

export const UploadedFilePreview = () => (
  <view.div
    data-testid="file-preview"
    uses={[styles.filePreviewStyles, styles.filePreviewStylesUnprocessed]}
  >
    <Icon icon={document} fill="currentColor" />
  </view.div>
);

const DownloadRemoveButton = ({
  disabled,
  disableDownload,
  downloadLink,
  onRemove,
}: {
  disabled?: boolean;
  disableDownload?: boolean;
  downloadLink?: string | null;
  onRemove?: () => void;
}) => {
  const intl = useIntl();
  if (disabled && downloadLink) {
    return (
      <Button
        href={downloadLink}
        disabled={disableDownload}
        type="icon"
        aria-label={intl.formatMessage(messages.download)}
        target="_blank"
      >
        <Icon icon={download} size="small" css={{color: 'action.primary'}} />
      </Button>
    );
  }
  if (onRemove) {
    return (
      <Button
        onPress={onRemove}
        disabled={disabled}
        type="icon"
        aria-label={intl.formatMessage(messages.remove)}
      >
        <Icon
          icon={cancel}
          size="small"
          css={{
            color: 'action.secondary',
            ':hover': {color: 'feedback.critical'},
          }}
        />
      </Button>
    );
  }

  return null;
};

export type UploadedFile = {
  id: string;
  category?: string;
  created?: number;
  localFile?: File;
  stripeFile?: StripeFileFragmentData; // returned from v1/files
};

type Props = {
  categories: {category: string; message: string}[];
  categoryError?: string;
  disabled?: boolean;
  onCategoryChange?: (
    fileId: string,
    newCategory?: string,
    previousCategory?: string,
  ) => void;
  onFileSuccessfullyUploaded: (
    fileId: string,
    stripeFile: StripeFileFragmentData,
  ) => void;
  /** Callback when there is an error uploading the file */
  onError?: (error: Error) => void;
  onRemove?: (fileId: string, category?: string) => void;
  purpose: string;
  selectedCategories?: Set<string>;
  showDivider: boolean;
  showFileSize?: boolean;
  showPreview?: boolean;
  uploadedFile: UploadedFile;
  consentedToShareWithPlatform?: boolean;
  disableDownload?: boolean;
};

export const FileUploaderFileListItem: React.FC<Props> = ({
  categories,
  categoryError,
  disabled,
  onCategoryChange,
  onFileSuccessfullyUploaded,
  onRemove,
  onError,
  purpose,
  selectedCategories,
  showDivider,
  showFileSize,
  showPreview,
  uploadedFile,
  consentedToShareWithPlatform,
  disableDownload,
}) => {
  const intl = useIntl();
  const {category, localFile, stripeFile} = uploadedFile;
  const {
    mutate: uploadFile,
    reset,
    status: uploadStatus,
    error: uploadError,
  } = useUploadFile(
    uploadedFile,
    purpose,
    onFileSuccessfullyUploaded,
    onError,
    consentedToShareWithPlatform,
  );
  const {
    refetch,
    status: retrieveStatus,
    data: retrievedFile,
  } = useRetrieveFile(uploadedFile, onFileSuccessfullyUploaded);

  const fileName: string | null | undefined =
    localFile?.name || stripeFile?.filename;
  const fileSize = localFile?.size || stripeFile?.size || 0;
  const downloadLink =
    stripeFile?.dashboard_url || retrievedFile?.dashboard_url;

  // We either upload OR retrieve the file, by checking the existence of the localFile property
  const status = localFile ? uploadStatus : retrieveStatus;
  const retry: () => void = localFile
    ? () => {
        reset();
        uploadFile();
      }
    : refetch;

  useEffect(() => {
    uploadFile(); // run once on mount to upload the file
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'error') {
    return (
      <UploadedFileErrorState
        disabled={disabled}
        fileName={fileName}
        retry={retry}
        remove={() => onRemove && onRemove(uploadedFile.id, category)}
        showDivider={showDivider}
        showPreview={showPreview}
        uploadError={!!uploadError}
      />
    );
  }

  // Only show loading state if file is being retrieved / uploaded
  if (status === 'loading' || (!stripeFile && !retrievedFile)) {
    return (
      <UploadedFileLoadingState
        fileName={fileName}
        showDivider={showDivider}
        showPreview={showPreview}
      />
    );
  }

  return (
    <>
      <view.div uses={[styles.fileContainerRowStyles]}>
        {showPreview && <UploadedFilePreview />}
        <view.div uses={[styles.fileContainerColumnStyles]}>
          <view.div uses={[styles.fileNameContainerStyles]}>
            <Icon icon={document} size="small" />
            <view.span
              uses={[styles.fileNameStyles, styles.fileNameStylesUnprocessed]}
            >
              {fileName || intl.formatMessage(messages.uploadedFilePlaceholder)}
            </view.span>
            <DownloadRemoveButton
              disabled={disabled}
              disableDownload={disableDownload}
              downloadLink={downloadLink}
              onRemove={() => onRemove && onRemove(uploadedFile.id, category)}
            />
          </view.div>

          {categories.length > 0 && (
            <view.div
              css={{
                font: 'body.small',
                color: 'text',
                stack: 'x',
                gap: 'xlarge',
                alignY: 'center',
              }}
            >
              <view.div
                css={{
                  stack: 'y',
                  gap: 'small',
                  width: 'fill',
                  paddingBottom: 'small',
                }}
              >
                <Select
                  aria-label={intl.formatMessage(messages.selectCategory)}
                  name="supporting_evidence_file_category"
                  onChange={(e) => {
                    if (onCategoryChange)
                      onCategoryChange(
                        uploadedFile.id,
                        e.target.value,
                        category,
                      );
                  }}
                  value={category}
                  disabled={disabled}
                  css={{
                    height: 'large',
                    minHeight: 'large',
                    paddingTop: 'xxsmall',
                    paddingBottom: 'xxsmall',
                  }}
                >
                  <view.option
                    label={intl.formatMessage(messages.selectCategory)}
                    value={undefined}
                  />
                  {categories.map(({category, message}) => {
                    return (
                      <view.option
                        key={`${uploadedFile.id}_${category}`}
                        disabled={
                          selectedCategories && selectedCategories.has(category)
                        }
                        label={message}
                        value={category}
                      />
                    );
                  })}
                </Select>
                {categoryError && !category && (
                  <view.span
                    css={{font: 'body.small', color: 'feedback.critical'}}
                  >
                    {categoryError}
                  </view.span>
                )}
              </view.div>
            </view.div>
          )}
          {showFileSize &&
            intl.formatMessage(messages.fileSize, {
              size: intl.formatNumber(fileSize / 1e6, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              }),
            })}
        </view.div>
      </view.div>
      {showDivider && <Divider uses={[styles.dividerStyles]} />}
    </>
  );
};
